import { ReactElement, use } from 'react';
import { useTranslation } from 'react-i18next';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import WanderingBoundaryIcon from 'assets/icons/wandering-boundary.svg?react';
import Constants from 'style/constants';
import Alert from 'shared/components/alert/alert';
import WanderingBoundaryPassageIcon from 'shared/components/icons/wandering-boundary-passage-icon';
import LoadingIndicator from 'shared/components/loading-indicator/loading-indicator';

import WanderingBoundary from 'features/wandering-boundary-settings/domain/models/wandering-boundary';
import WanderingBoundaryPassage from 'features/wandering-boundary-settings/domain/models/wandering-boundary-passage';
import WanderingBoundarySettingsProviderContext from 'features/wandering-boundary-settings/providers/contexts/wandering-boundary-provider-context';
import WanderingBoundaryPassageRowProvider from 'features/wandering-boundary-settings/providers/wandering-boundary-passage-row-provider';
import WanderingBoundaryRowProvider from 'features/wandering-boundary-settings/providers/wandering-boundary-row-provider';
import WanderingBoundaryPassageRow from 'features/wandering-boundary-settings/views/wandering-boundary-passage-row/wandering-boundary-passage-row.component';
import WanderingBoundaryRow from 'features/wandering-boundary-settings/views/wandering-boundary-row/wandering-boundary-row.component';
import WanderingBoundarySettingsDetailsHeader from 'features/wandering-boundary-settings/views/wandering-boundary-settings-details/wandering-boundary-settings-details-header';
import styles from './wandering-boundary-settings-details.module.scss';

const WanderingBoundarySettingsDetails = (): ReactElement => {
  const { detailsState } = use(WanderingBoundarySettingsProviderContext);
  const {
    selectedLocation,
    isLoading,
    addWanderingBoundary,
    wanderingBoundaries,
    addWanderingBoundaryPassage,
    wanderingBoundaryPassages,
    isEditing,
  } = detailsState;

  const { t } = useTranslation('wanderingBoundarySettings');

  const hasAnyWanderingBoundaries = wanderingBoundaries && wanderingBoundaries.some((boundary) => boundary.id);

  const listTitleRow = (title: string, icon: ReactElement): ReactElement => (
    <div className={styles.listTitleRow}>
      {icon}
      <Typography variant="h5">{title}</Typography>
    </div>
  );

  const addWanderingBoundaryButton = (): ReactElement =>
    isEditing ? (
      <></>
    ) : (
      <Button variant="text" fullWidth onClick={addWanderingBoundary}>
        <AddCircleOutlineIcon sx={{ marginRight: 1 }} fontSize="small" />
        <Typography variant="body1" color="primary">
          {t('details.addWanderingBoundaryButton')}
        </Typography>
      </Button>
    );

  const noWanderingBoundariesText = (): ReactElement =>
    !hasAnyWanderingBoundaries ? <Typography variant="body2">{t('details.noWanderingBoundariesMessage')}</Typography> : <></>;

  const addWanderingBoundaryPassageButton = (): ReactElement =>
    isEditing || !hasAnyWanderingBoundaries ? (
      <></>
    ) : (
      <Button variant="text" fullWidth onClick={addWanderingBoundaryPassage}>
        <AddCircleOutlineIcon sx={{ marginRight: 1 }} fontSize="small" />
        <Typography variant="body1" color="primary">
          {t('details.addWanderingBoundaryPassageButton')}
        </Typography>
      </Button>
    );

  const wanderingBoundariesListItem = (wanderingBoundary: WanderingBoundary): ReactElement => {
    const viewingMode = wanderingBoundary.id ? 'viewing' : 'creation';
    return (
      <WanderingBoundaryRowProvider
        key={wanderingBoundary.key ?? wanderingBoundary.id}
        wanderingBoundary={wanderingBoundary}
        viewingMode={viewingMode}
      >
        <WanderingBoundaryRow />
      </WanderingBoundaryRowProvider>
    );
  };

  const wanderingBoundaryPassagesListItem = (wanderingBoundaryPassage: WanderingBoundaryPassage): ReactElement => {
    const viewingMode = wanderingBoundaryPassage.id ? 'viewing' : 'creation';
    return (
      <WanderingBoundaryPassageRowProvider
        key={wanderingBoundaryPassage.key ?? wanderingBoundaryPassage.id}
        wanderingBoundaryPassage={wanderingBoundaryPassage}
        viewingMode={viewingMode}
      >
        <WanderingBoundaryPassageRow />
      </WanderingBoundaryPassageRowProvider>
    );
  };

  return (
    <div className={`${styles.container} ${selectedLocation ? styles.isOpen : ''}`}>
      <WanderingBoundarySettingsDetailsHeader />
      <div className={styles.content}>
        {isLoading ? (
          <LoadingIndicator />
        ) : (
          <>
            <Alert title={t('details.explanationTitle')} body={t('details.explanationBody')} severity={'info'} />
            <div className={styles.contentContainer}>
              {listTitleRow(t('details.wanderingBoundaryListTitle'), <WanderingBoundaryIcon className={styles.wanderingBoundaryIcon} />)}
              {wanderingBoundaries?.map((wanderingBoundary) => wanderingBoundariesListItem(wanderingBoundary))}
              {addWanderingBoundaryButton()}
              {listTitleRow(
                t('details.wanderingBoundaryPassagesListTitle'),
                <WanderingBoundaryPassageIcon color={Constants.Colors.onSurface} />,
              )}
              {wanderingBoundaryPassages?.map((wanderingBoundaryPassage) => wanderingBoundaryPassagesListItem(wanderingBoundaryPassage))}
              {noWanderingBoundariesText()}
              {addWanderingBoundaryPassageButton()}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default WanderingBoundarySettingsDetails;
